import { useEffect } from "react";
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from "@floating-ui/react-dom";
import usePopoverDismiss from "./usePopoverDismiss.js";
import useReassignmentCandidates from "./useReassignmentCandidates.js";
import {
  isPlanningStateChangeInScope,
  readPlanningStateChangeScopeFromLocation,
  subscribePlanningStateChange,
} from "../utils/planningCrossViewSync.js";

export default function useReassignmentPopoverController({
  anchorElement = null,
  contexts = [],
  error = null,
  isOpen = true,
  loading = false,
  onClose,
  onReassign,
  placement = "bottom-start",
  rebuildMode = "complete",
  stepId = null,
  stepType = null,
}) {
  const { refs, floatingStyles, isPositioned } = useFloating({
    open: isOpen,
    placement,
    middleware: [offset(12), flip({ padding: 12 }), shift({ padding: 12 })],
    whileElementsMounted: autoUpdate,
  });
  const candidateState = useReassignmentCandidates({
    contexts,
    error,
    isOpen,
    loading,
    rebuildMode,
    stepId,
    stepType,
  });

  useEffect(() => {
    refs.setReference(anchorElement);
  }, [anchorElement, refs]);

  usePopoverDismiss({
    anchorElement,
    floatingRef: refs.floating,
    isOpen,
    onClose,
  });

  useEffect(() => subscribePlanningStateChange((detail) => {
    if (
      !isPlanningStateChangeInScope(
        detail,
        readPlanningStateChangeScopeFromLocation(),
      )
    ) {
      return;
    }
    if (
      detail?.reasonCode === "runtime_revision_conflict"
      || detail?.reasonCode === "planning_mutation_in_progress"
    ) {
      onClose?.();
    }
  }), [onClose]);

  return {
    contentProps: {
      hasDisplayedCompatibilities: candidateState.hasDisplayedCompatibilities,
      impactCalculationStatus: candidateState.impactCalculationStatus,
      informationText: candidateState.informationText,
      isLoading: candidateState.isLoading,
      onReassign,
      remoteError: candidateState.remoteError,
      resolvedContexts: candidateState.resolvedContexts,
      resolvedStep: candidateState.resolvedStep,
      shouldRenderCompatibilities: candidateState.shouldRenderCompatibilities,
      showRouteImpact: candidateState.showRouteImpact,
    },
    floatingStyles,
    headerProps: {
      isLoading: candidateState.isLoading,
      isRouteImpactPending: candidateState.isRouteImpactPending,
      modeToggleDisabled: candidateState.modeToggleDisabled,
      modeToggleLabel: candidateState.modeToggleLabel,
      onClose,
      onToggleRouteImpact: candidateState.toggleRouteImpact,
      resolvedContexts: candidateState.resolvedContexts,
      resolvedStep: candidateState.resolvedStep,
      showRouteImpact: candidateState.showRouteImpact,
      stepLabel: candidateState.stepLabel,
    },
    isPositioned,
    popoverWidthClass: candidateState.popoverWidthClass,
    shouldShowLoadingState: candidateState.shouldShowLoadingState,
    setFloatingRef: refs.setFloating,
  };
}
